import React, { useState } from 'react';

export function FormModal({ onClose, onSave, onDelete, initialItem, modalType, sgdRate, lkrRate, usdRate, title }) {
    const getInitialData = () => {
        if (initialItem) return initialItem;

        if (modalType === 'gold-inventory') {
            return { type: 'Ring', name: '', quality: '22K', owner: '', weight: 0, pawn: 0, comment: '' };
        } else if (modalType === 'personal-loan') {
            return {
                name: '', type: 'Home Loan', totalAmount: 0, tenor: 0,
                principalPaid: 0, principalRemaining: 0, monthsLeft: 0,
                monthlyInstallment: 0, settlementDate: '',
                currency: 'SGD',        // Bug Fix #1a — required by scorecard & FI report filters
                paymentHistory: [],      // Bug Fix #1b — prevents undefined written to Drive
            };
        } else if (modalType === 'gold-loan') {
            return {
                name: '', bank: '', owner: '', goldWeight: 0, totalAmount: 0,
                unpaidInterest: 0, principalRemaining: 0, monthlyRate: 0, monthlyInterest: 0,
                currency: 'LKR',        // Bug Fix #1a
                paymentHistory: [],      // Bug Fix #1b
            };
        } else if (modalType === 'account') {
            return {
                accountName: '', accountNumber: '', bank: '', country: 'Singapore',
                currency: 'SGD', balanceSGD: 0, balanceUSD: 0, balanceLKR: 0
            };
        } else if (modalType === 'credit-card') {
            return {
                cardName: '', cardNumber: '', bank: '', country: 'Singapore',
                currency: 'SGD', balanceSGD: 0, balanceUSD: 0, balanceLKR: 0, dueDate: ''
            };
        } else if (modalType === 'income') {
            return {
                name: '', category: 'salary', amount: 0, currency: 'SGD',
                frequency: 'monthly', enabled: true, notes: '',
            };
        }
    };


    const [formData, setFormData] = useState(getInitialData());

    const handleChange = (field, value) => {
        setFormData(prev => {
            const updated = { ...prev, [field]: value };

            // Auto-calculate pawn for gold inventory
            // Only compute when value is a non-empty, valid number
            if (modalType === 'gold-inventory') {
                const numVal = parseFloat(value);
                if (!isNaN(numVal)) {
                    if (field === 'weight') updated.pawn = parseFloat((numVal / 8).toFixed(3));
                    if (field === 'pawn') updated.weight = parseFloat((numVal * 8).toFixed(1));
                }
            }

            // Auto-calculate balances for accounts and credit cards
            if (modalType === 'account' || modalType === 'credit-card') {
                const primaryCurrency = updated.currency || 'SGD';
                const numVal = parseFloat(value);

                // When primary currency changes, recalculate all balances
                if (field === 'currency') {
                    const oldPrimary = prev.currency || 'SGD';
                    const oldBalance = parseFloat(prev[`balance${oldPrimary}`]) || 0;

                    updated[`balance${primaryCurrency}`] = oldBalance;

                    if (primaryCurrency === 'SGD') {
                        updated.balanceUSD = parseFloat((oldBalance / usdRate).toFixed(2));
                        updated.balanceLKR = parseFloat((oldBalance * lkrRate).toFixed(2));
                    } else if (primaryCurrency === 'USD') {
                        updated.balanceSGD = parseFloat((oldBalance * usdRate).toFixed(2));
                        updated.balanceLKR = parseFloat((oldBalance * usdRate * lkrRate).toFixed(2));
                    } else if (primaryCurrency === 'LKR') {
                        updated.balanceSGD = parseFloat((oldBalance / lkrRate).toFixed(2));
                        updated.balanceUSD = parseFloat((oldBalance / lkrRate / usdRate).toFixed(2));
                    }
                }

                // When primary balance changes, auto-calculate others
                // Only recalculate if there's a real number (allow empty string to pass through)
                if (field === `balance${primaryCurrency}` && !isNaN(numVal)) {
                    if (primaryCurrency === 'SGD') {
                        updated.balanceUSD = parseFloat((numVal / usdRate).toFixed(2));
                        updated.balanceLKR = parseFloat((numVal * lkrRate).toFixed(2));
                    } else if (primaryCurrency === 'USD') {
                        updated.balanceSGD = parseFloat((numVal * usdRate).toFixed(2));
                        updated.balanceLKR = parseFloat((numVal * usdRate * lkrRate).toFixed(2));
                    } else if (primaryCurrency === 'LKR') {
                        updated.balanceSGD = parseFloat((numVal / lkrRate).toFixed(2));
                        updated.balanceUSD = parseFloat((numVal / lkrRate / usdRate).toFixed(2));
                    }
                }
            }

            return updated;
        });
    };

    // Helper: convert formData numeric fields to actual numbers before saving
    const numericFields = [
        'weight', 'pawn', 'totalAmount', 'tenor', 'principalPaid', 'principalRemaining',
        'monthsLeft', 'monthlyInstallment', 'goldWeight', 'unpaidInterest', 'monthlyRate',
        'monthlyInterest', 'balanceSGD', 'balanceUSD', 'balanceLKR',
        'amount'  // INC-06: income amount
    ];
    const coerceFormData = (data) => {
        const out = { ...data };
        numericFields.forEach(f => {
            if (f in out) out[f] = parseFloat(out[f]) || 0;
        });
        return out;
    };


    const handleSubmit = () => {
        if (modalType === 'gold-inventory' && (!formData.name || formData.weight <= 0)) {
            alert('Please fill in required fields');
            return;
        }
        if (modalType === 'account' && (!formData.accountName || !formData.accountNumber)) {
            alert('Please fill in required fields');
            return;
        }
        if (modalType === 'credit-card' && (!formData.cardName || !formData.cardNumber)) {
            alert('Please fill in required fields');
            return;
        }
        if ((modalType === 'personal-loan' || modalType === 'gold-loan') && !formData.name) {
            alert('Please fill in required fields');
            return;
        }
        if (modalType === 'income' && (!formData.name || parseFloat(formData.amount) <= 0)) {
            alert('Please enter a name and a positive amount');
            return;
        }
        onSave(coerceFormData(formData));
    };


    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <div className="modal-title">{title}</div>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                {modalType === 'gold-inventory' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Item Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., Gold Ring"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value)}
                                >
                                    <option value="Ring">Ring</option>
                                    <option value="Bangle">Bangle</option>
                                    <option value="Chain">Chain</option>
                                    <option value="Pendant">Pendant</option>
                                    <option value="Earring">Earring</option>
                                    <option value="Bracelet">Bracelet</option>
                                    <option value="Anklet">Anklet</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Quality</label>
                                <select
                                    className="form-select"
                                    value={formData.quality}
                                    onChange={(e) => handleChange('quality', e.target.value)}
                                >
                                    <option value="22K">22K</option>
                                    <option value="18K">18K</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Owner *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.owner}
                                onChange={(e) => handleChange('owner', e.target.value)}
                                placeholder="Owner name"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Weight (g) *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.weight ?? ''}
                                    onChange={(e) => handleChange('weight', e.target.value)}
                                    step="0.1"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Pawn</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.pawn ?? ''}
                                    onChange={(e) => handleChange('pawn', e.target.value)}
                                    step="0.001"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Comment</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.comment}
                                onChange={(e) => handleChange('comment', e.target.value)}
                                placeholder="Optional notes"
                            />
                        </div>
                    </>
                )}

                {modalType === 'personal-loan' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Loan Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., SC Home Loan"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Type</label>
                                <select
                                    className="form-select"
                                    value={formData.type}
                                    onChange={(e) => handleChange('type', e.target.value)}
                                >
                                    <option value="Home Loan">Home Loan</option>
                                    <option value="Personal Loan">Personal Loan</option>
                                    <option value="Tax Loan">Tax Loan</option>
                                    <option value="Car Loan">Car Loan</option>
                                    <option value="Baby Loan">Baby Loan</option>
                                    <option value="Education Loan">Education Loan</option>
                                    <option value="Credit Card Loan">Credit Card Loan</option>
                                    <option value="Renovation Loan">Renovation Loan</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            {/* BUG-005: currency selector — previously missing, defaulted SGD only */}
                            <div className="form-group">
                                <label className="form-label">Currency</label>
                                <select
                                    className="form-select"
                                    value={formData.currency || 'SGD'}
                                    onChange={(e) => handleChange('currency', e.target.value)}
                                >
                                    <option value="SGD">SGD</option>
                                    <option value="LKR">LKR</option>
                                    <option value="USD">USD</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Total Amount ({formData.currency || 'SGD'})</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.totalAmount ?? ''}
                                    onChange={(e) => handleChange('totalAmount', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Tenor (years)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.tenor ?? ''}
                                    onChange={(e) => handleChange('tenor', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Paid ({formData.currency || 'SGD'})</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.principalPaid ?? ''}
                                    onChange={(e) => handleChange('principalPaid', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Remaining ({formData.currency || 'SGD'})</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.principalRemaining ?? ''}
                                    onChange={(e) => handleChange('principalRemaining', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Months Left</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.monthsLeft ?? ''}
                                    onChange={(e) => handleChange('monthsLeft', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Monthly ({formData.currency || 'SGD'})</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.monthlyInstallment ?? ''}
                                    onChange={(e) => handleChange('monthlyInstallment', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Settlement Date</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.settlementDate}
                                onChange={(e) => handleChange('settlementDate', e.target.value)}
                                placeholder="e.g., Apr-29"
                            />
                        </div>
                    </>
                )}

                {modalType === 'gold-loan' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Item Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., Gold Pawn Bangle"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Owner</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.owner}
                                    onChange={(e) => handleChange('owner', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bank</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.bank}
                                    onChange={(e) => handleChange('bank', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Gold Weight (g)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.goldWeight ?? ''}
                                onChange={(e) => handleChange('goldWeight', e.target.value)}
                                step="0.01"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Borrowed (₨)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.totalAmount ?? ''}
                                    onChange={(e) => handleChange('totalAmount', e.target.value)}
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Unpaid Interest (₨)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.unpaidInterest ?? ''}
                                    onChange={(e) => handleChange('unpaidInterest', e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Principal Remaining (₨)</label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData.principalRemaining ?? ''}
                                onChange={(e) => handleChange('principalRemaining', e.target.value)}
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Monthly Rate (%)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.monthlyRate ?? ''}
                                    onChange={(e) => handleChange('monthlyRate', e.target.value)}
                                    step="0.01"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Monthly Interest (₨)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.monthlyInterest ?? ''}
                                    onChange={(e) => handleChange('monthlyInterest', e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                )}

                {modalType === 'account' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Account Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.accountName}
                                onChange={(e) => handleChange('accountName', e.target.value)}
                                placeholder="e.g., OCBC Savings Account"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Account Number *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.accountNumber}
                                    onChange={(e) => handleChange('accountNumber', e.target.value)}
                                    placeholder="Account number"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bank *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.bank}
                                    onChange={(e) => handleChange('bank', e.target.value)}
                                    placeholder="Bank name"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Country</label>
                                <select
                                    className="form-select"
                                    value={formData.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                >
                                    <option value="Singapore">Singapore</option>
                                    <option value="Sri Lanka">Sri Lanka</option>
                                    <option value="USA">USA</option>
                                    <option value="UK">UK</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Currency</label>
                                <select
                                    className="form-select"
                                    value={formData.currency}
                                    onChange={(e) => handleChange('currency', e.target.value)}
                                >
                                    <option value="SGD">SGD</option>
                                    <option value="USD">USD</option>
                                    <option value="LKR">LKR</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Available Balance ({formData.currency || 'SGD'}) *
                            </label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData[`balance${formData.currency || 'SGD'}`] ?? ''}
                                onChange={(e) => handleChange(`balance${formData.currency || 'SGD'}`, e.target.value)}
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>

                        {formData.currency !== 'SGD' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (SGD)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceSGD ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}

                        {formData.currency !== 'USD' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (USD)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceUSD ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}

                        {formData.currency !== 'LKR' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (LKR)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceLKR ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}
                    </>
                )}

                {modalType === 'credit-card' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Card Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.cardName}
                                onChange={(e) => handleChange('cardName', e.target.value)}
                                placeholder="e.g., OCBC Titanium Rewards"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Card Number *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.cardNumber}
                                    onChange={(e) => handleChange('cardNumber', e.target.value)}
                                    placeholder="Last 4 digits or full number"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Bank *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.bank}
                                    onChange={(e) => handleChange('bank', e.target.value)}
                                    placeholder="Bank name"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Country</label>
                                <select
                                    className="form-select"
                                    value={formData.country}
                                    onChange={(e) => handleChange('country', e.target.value)}
                                >
                                    <option value="Singapore">Singapore</option>
                                    <option value="Sri Lanka">Sri Lanka</option>
                                    <option value="USA">USA</option>
                                    <option value="UK">UK</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Currency</label>
                                <select
                                    className="form-select"
                                    value={formData.currency}
                                    onChange={(e) => handleChange('currency', e.target.value)}
                                >
                                    <option value="SGD">SGD</option>
                                    <option value="USD">USD</option>
                                    <option value="LKR">LKR</option>
                                    <option value="GBP">GBP</option>
                                    <option value="EUR">EUR</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Due Date</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.dueDate}
                                onChange={(e) => handleChange('dueDate', e.target.value)}
                                placeholder="e.g., 15th of every month"
                            />
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                Outstanding Balance ({formData.currency || 'SGD'}) *
                            </label>
                            <input
                                type="number"
                                className="form-input"
                                value={formData[`balance${formData.currency || 'SGD'}`] ?? ''}
                                onChange={(e) => handleChange(`balance${formData.currency || 'SGD'}`, e.target.value)}
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>

                        {formData.currency !== 'SGD' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (SGD)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceSGD ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}

                        {formData.currency !== 'USD' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (USD)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceUSD ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}

                        {formData.currency !== 'LKR' && (
                            <div className="form-group">
                                <label className="form-label">Equivalent Balance (LKR)</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.balanceLKR ?? 0}
                                    readOnly
                                    disabled
                                    style={{ backgroundColor: 'var(--bg-secondary)', cursor: 'not-allowed' }}
                                />
                            </div>
                        )}
                    </>
                )}

                {/* INC-07: Income form */}
                {modalType === 'income' && (
                    <>
                        <div className="form-group">
                            <label className="form-label">Income Name *</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                placeholder="e.g., OCBC Salary, Rental — Colombo flat"
                            />
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Category</label>
                                <select
                                    className="form-select"
                                    value={formData.category}
                                    onChange={(e) => handleChange('category', e.target.value)}
                                >
                                    <option value="salary">Salary</option>
                                    <option value="freelance">Freelance / Side</option>
                                    <option value="rental">Rental</option>
                                    <option value="dividends">Dividends / Interest</option>
                                    <option value="business">Business</option>
                                    <option value="bonus">Bonus / Variable</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">Frequency</label>
                                <select
                                    className="form-select"
                                    value={formData.frequency}
                                    onChange={(e) => handleChange('frequency', e.target.value)}
                                >
                                    <option value="monthly">Monthly</option>
                                    <option value="quarterly">Quarterly</option>
                                    <option value="annual">Annual</option>
                                    <option value="one-time">One-time</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">Amount *</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    value={formData.amount ?? ''}
                                    onChange={(e) => handleChange('amount', e.target.value)}
                                    step="0.01"
                                    placeholder="0.00"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Currency</label>
                                <select
                                    className="form-select"
                                    value={formData.currency}
                                    onChange={(e) => handleChange('currency', e.target.value)}
                                >
                                    <option value="SGD">SGD</option>
                                    <option value="USD">USD</option>
                                    <option value="LKR">LKR</option>
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="form-label">Notes</label>
                            <input
                                type="text"
                                className="form-input"
                                value={formData.notes || ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                placeholder="Optional — e.g., post-tax, includes CPF"
                            />
                        </div>

                        <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <input
                                type="checkbox"
                                id="income-enabled"
                                checked={formData.enabled !== false}
                                onChange={(e) => handleChange('enabled', e.target.checked)}
                            />
                            <label htmlFor="income-enabled" style={{ fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                                Active — include in cash-flow calculations
                            </label>
                        </div>
                    </>
                )}

                <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
                <button className="btn btn-primary" onClick={handleSubmit}>
                    {initialItem ? 'Save' : 'Add'}
                </button>


                {onDelete && (
                    <button className="btn btn-danger" onClick={() => {
                        onDelete();
                        onClose();
                    }}>
                        Delete
                    </button>
                )}
            </div>
        </div>
    );
}